import { useEffect } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { OrderSchemaType, ItemPath } from '../order.schema';
import { OrderItemType } from '../types';

interface useItemSubtotalCalculatorProps {
  itemPath: ItemPath;
  itemType: OrderItemType;
  quantity: number;
}

interface ItemSubtotalCalculatorResult {
  unitPrice: number;
  itemTaxes: number;
  itemSubtotal: number;
}

export const useItemSubtotalCalculator = ({ itemPath, itemType, quantity }: useItemSubtotalCalculatorProps): ItemSubtotalCalculatorResult => {
  const { watch, setValue, control } = useFormContext<OrderSchemaType>();

  const priceDetailsPath = `${itemPath}.priceDetails` as const;
  const offeredProductMaterialPath = `${itemPath}.offeredProductMaterial` as const;

  const [weight, pricePerKgFinal, unitPrice, discountPercent, itemSubtotal, itemTaxes] = watch([
    `${offeredProductMaterialPath}.weigth`,
    `${priceDetailsPath}.pricePerKgFinal`,
    `${priceDetailsPath}.unitPrice`,
    `${priceDetailsPath}.discount`,
    `${priceDetailsPath}.itemSubtotal`,
    `${priceDetailsPath}.itemTaxes`
  ]);

  const subItems = useWatch({
    control,
    name: `${itemPath}.subItems` as any,
  });

  useEffect(() => {
    const numQty = Number(quantity) || 0;
    const numWeight = parseFloat(String(weight) || '0');
    const numFinalPriceKg = pricePerKgFinal ?? 0;
    const numUnitPrice = unitPrice ?? 0;
    const numDiscount = Number(String(discountPercent || '').replace(',', '.')) || 0;
    let calculatedSubtotal = 0;

    if (itemType === OrderItemType.MATERIAL) {
      if (numQty > 0) {
        if (numUnitPrice > 0) {
          calculatedSubtotal = numUnitPrice * numQty;
        } else if (numFinalPriceKg > 0 && numWeight > 0) {
          calculatedSubtotal = numFinalPriceKg * numWeight * numQty;
        }
      }
    } else if (itemType) {
      if (numQty > 0 && numUnitPrice > 0) {
        calculatedSubtotal = numUnitPrice * numQty;
      }
    }

    if (subItems && Array.isArray(subItems)) {
      (subItems as any[]).forEach((subItem: any) => {
        if (subItem.itemType === 'SERVICE') {
          const serviceTotal = Number(subItem.priceDetails?.itemSubtotal) || 0;
          calculatedSubtotal += serviceTotal;
        }
      });
    }

    if (calculatedSubtotal > 0 && numDiscount > 0 && numDiscount <= 100) {
      calculatedSubtotal = calculatedSubtotal * (1 - numDiscount / 100);
    }

    const currentSubtotalState = watch(`${priceDetailsPath}.itemSubtotal`);

    if (currentSubtotalState !== calculatedSubtotal) {
      setValue(`${priceDetailsPath}.itemSubtotal`, parseFloat(calculatedSubtotal.toFixed(2)), {
        shouldValidate: true,
        shouldDirty: true,
      });
    }
  }, [itemType, quantity, weight, pricePerKgFinal, unitPrice, discountPercent, subItems, setValue, itemPath, priceDetailsPath, watch]);
  
  return {
    unitPrice: unitPrice ?? 0,
    itemTaxes: itemTaxes ?? 0,
    itemSubtotal: itemSubtotal ?? 0,
  };
};